/**
 * Fixed-position anchoring for portaled overlay panels (menus, calc tooltips, header hints).
 *
 * Overlays used to be `position: absolute` children of their trigger, which put them inside the
 * nearest `.card { overflow: hidden }` island and got them clipped at table and card edges. They
 * now render into `document.body` and take their coordinates from the trigger's viewport rect —
 * this hook is the shared bit of arithmetic, plus the clamping that keeps a panel on screen and
 * the listeners that keep it glued to the trigger while the page scrolls.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';

/** Panel left edge on the trigger's left / right edge on its right / centered on it. */
export type AnchorAlign = 'start' | 'end' | 'center';

export type AnchoredOptions = {
  align: AnchorAlign;
  /** Vertical distance between the trigger's bottom edge and the panel's top edge. */
  gapPx: number;
};

/** Keep-out margin from the viewport edges when a panel would overflow. */
const VIEWPORT_MARGIN = 8;

/**
 * Off-screen but laid out: the panel needs a fixed box before it can be measured, otherwise a
 * `min-width` panel dropped into the body flow measures as wide as the page.
 */
const PRE_MEASURE: CSSProperties = { position: 'fixed', top: 0, left: 0, visibility: 'hidden' };

type Point = { top: number; left: number };

/**
 * Returns the inline style for the panel while `open`, `null` otherwise. The panel is rendered
 * invisible for the single frame it takes to measure it, so it never flashes at the corner.
 */
export function useAnchoredPosition(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  { align, gapPx }: AnchoredOptions,
): CSSProperties | null {
  const [point, setPoint] = useState<Point | null>(null);

  const update = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (trigger === null || panel === null) return;

    const rect = trigger.getBoundingClientRect();
    const { width, height } = panel.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    const anchor =
      align === 'start' ? rect.left : align === 'end' ? rect.right : rect.left + rect.width / 2;
    const offset = align === 'start' ? 0 : align === 'end' ? width : width / 2;
    const maxLeft = Math.max(VIEWPORT_MARGIN, viewportWidth - width - VIEWPORT_MARGIN);
    const left = Math.min(Math.max(anchor - offset, VIEWPORT_MARGIN), maxLeft);

    // Below the trigger by default; flipped above it when the panel would run off the bottom.
    let top = rect.bottom + gapPx;
    if (top + height > viewportHeight - VIEWPORT_MARGIN) {
      const above = rect.top - gapPx - height;
      top = above >= VIEWPORT_MARGIN ? above : Math.max(VIEWPORT_MARGIN, maxTop(viewportHeight, height));
    }

    setPoint({ top, left });
  }, [align, gapPx, panelRef, triggerRef]);

  useLayoutEffect(() => {
    if (!open) {
      setPoint(null);
      return;
    }
    update();
  }, [open, update]);

  useEffect(() => {
    if (!open) return;
    // Capture phase so inner scrollers (.posts__grid, .table-wrap, .menu__checklist) count too.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, update]);

  if (!open) return null;
  return point === null ? PRE_MEASURE : { position: 'fixed', top: point.top, left: point.left };
}

function maxTop(viewportHeight: number, panelHeight: number): number {
  return viewportHeight - panelHeight - VIEWPORT_MARGIN;
}
